import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, WifiSlash as WifiOff, Money as Banknote, UsersThree as UsersRound, Package as Boxes, ChartPie as PieChart, GitBranch, ShieldCheck } from '@phosphor-icons/react';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import SEO, { buildFaqJsonLd } from '../../components/SEO';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { node.classList.add('revealed'); obs.unobserve(node); } },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);
  return ref;
};

const R = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return <div ref={ref} className={`reveal-on-scroll ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const NIGERIA_STATES = [
  'Lagos', 'Abuja (FCT)', 'Rivers', 'Kano', 'Oyo', 'Anambra', 'Delta',
  'Enugu', 'Ogun', 'Kaduna', 'Ondo', 'Edo', 'Imo', 'Kwara', 'Plateau',
  'Osun', 'Abia', 'Akwa Ibom', 'Nasarawa', 'Bauchi', 'Cross River',
  'Ebonyi', 'Benue', 'Kogi', 'Niger', 'Sokoto', 'Kebbi', 'Zamfara',
  'Jigawa', 'Katsina', 'Yobe', 'Borno', 'Adamawa', 'Gombe', 'Taraba',
  'Bayelsa', 'Ekiti',
];

const NIGERIA_FAQS = [
  {
    question: 'Does Smart POS Africa work during NEPA outages in Nigeria?',
    answer: 'Yes. Smart POS Africa works fully offline. Every sale is saved on the device and syncs automatically when power and internet return. NEPA taking light will not stop your business from recording transactions.',
  },
  {
    question: 'Does Smart POS Africa support bank transfers in Nigeria?',
    answer: 'Yes. Smart POS Africa supports cash, bank transfer, card, and mobile money payments. You can accept split payments – for example, part cash and part bank transfer – all in a single transaction, with each method recorded separately.',
  },
  {
    question: 'How much does Smart POS Africa cost in Nigeria?',
    answer: 'Smart POS Africa starts at NGN 20,000 per month, or NGN 200,000 per year (saving 17%). Every plan includes all features, unlimited staff accounts, and multi-branch support. There are no hidden fees and no per-user charges. A one-month free trial is available with no credit card required.',
  },
  {
    question: 'Can I track what each staff member is selling in Nigeria?',
    answer: 'Yes. Every transaction is linked to the staff member who processed it. You can see each cashier\'s sales, the discounts they applied, and their performance over any period. This gives you full accountability without needing to watch the counter yourself.',
  },
  {
    question: 'Can Smart POS Africa handle a high-volume restaurant or retail shop in Lagos?',
    answer: 'Yes. Smart POS Africa is designed for speed – product search, discount application, payment, and receipt printing take seconds. It handles hundreds of transactions a day without slowing down, even on a standard Android phone.',
  },
  {
    question: 'Do I need special hardware to use Smart POS Africa in Nigeria?',
    answer: 'No. Smart POS Africa runs in any browser on Android phones, tablets, or computers. No special POS hardware is required. If you have a Bluetooth or USB receipt printer, it works with those too – but you can start with just a phone.',
  },
];

const nigeriaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Smart POS Africa',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Android, iOS',
  description: 'Best POS software for Nigerian businesses. Works offline during NEPA outages, supports bank transfers, full staff accountability. NGN pricing from NGN 20,000/month.',
  url: 'https://smartposafrica.com/nigeria',
  areaServed: { '@type': 'Country', name: 'Nigeria' },
  offers: {
    '@type': 'Offer',
    price: '20000',
    priceCurrency: 'NGN',
    description: 'From NGN 20,000/month. One month free trial, no credit card required.',
  },
};

const features = [
  {
    icon: WifiOff,
    accent: 'bg-emerald-500/[0.08]',
    iconColor: 'text-emerald-600',
    title: 'Works during NEPA outages',
    description: 'Full offline mode – every sale is saved locally on the device. No internet, no problem. Data syncs the moment your connection returns.',
  },
  {
    icon: Banknote,
    accent: 'bg-yellow-500/[0.08]',
    iconColor: 'text-yellow-600',
    title: 'Bank transfer & card payments',
    description: 'Accept cash, bank transfers, card, and mobile money in a single transaction. Every payment method tracked and reported separately.',
  },
  {
    icon: ShieldCheck,
    accent: 'bg-rose-500/[0.08]',
    iconColor: 'text-rose-600',
    title: 'Staff accountability',
    description: 'Every transaction tied to a named staff member. See exactly who sold what, which discounts were applied, and flag discrepancies instantly.',
  },
  {
    icon: Boxes,
    accent: 'bg-amber-500/[0.08]',
    iconColor: 'text-amber-600',
    title: 'Inventory management',
    description: 'Stock levels update automatically with every sale across every branch. Know what to restock before you run out.',
  },
  {
    icon: PieChart,
    accent: 'bg-blue-500/[0.08]',
    iconColor: 'text-blue-600',
    title: 'Real-time analytics',
    description: 'Live revenue, profit, and expense data. Get a daily email summary every evening so you always know your numbers.',
  },
  {
    icon: GitBranch,
    accent: 'bg-violet-500/[0.08]',
    iconColor: 'text-violet-600',
    title: 'Multi-branch management',
    description: 'One dashboard for all your Lagos, Abuja, or Port Harcourt branches. Consolidated sales, inventory, and staff performance in one place.',
  },
];

const NigeriaPage = () => {
  const faqJsonLd = buildFaqJsonLd(NIGERIA_FAQS.map(({ question, answer }) => ({ question, answer })));

  return (
    <div className="min-h-screen bg-primaryB-50 overflow-x-hidden">
      <SEO
        title="Best POS Software in Nigeria"
        description="Smart POS Africa is the best POS software for Nigerian businesses. Works offline during NEPA outages, supports bank transfers, full staff accountability. Priced in NGN from NGN 20,000/month."
        path="/nigeria"
        keywords="best pos software nigeria, pos software nigeria, pos system nigeria, point of sale nigeria, retail pos nigeria, restaurant pos nigeria, pos nigeria 2026, pos software lagos, pos software abuja, staff accountability pos nigeria"
        jsonLd={[nigeriaJsonLd, faqJsonLd]}
        geoRegion="NG"
        geoPlacename="Nigeria"
      />
      <LandingNav />

      {/* ━━━ HERO ━━━ */}
      <section className="pt-24 sm:pt-32 lg:pt-40 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <R>
              <div className="inline-flex items-center gap-2 bg-emerald-500/[0.08] border border-emerald-500/[0.12] rounded-full px-4 py-1.5 mb-5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                <span className="text-emerald-800 text-xs font-bold tracking-wide uppercase">Nigeria</span>
              </div>
            </R>
            <R delay={80}>
              <h1 className="text-3xl sm:text-4xl lg:text-[3.25rem] font-extrabold text-primaryB-900 leading-[1.35] tracking-tight mb-6">
                Best POS Software in Nigeria
              </h1>
            </R>
            <R delay={150}>
              <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed max-w-xl mb-8">
                Built for Nigerian businesses – works when NEPA takes light, accepts bank transfers, keeps your staff accountable, and gives you real numbers every day.
              </p>
            </R>
            <R delay={200}>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-2xl transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Start free trial <ArrowRight weight="bold" className="w-4 h-4" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 bg-primaryB-100 hover:bg-primaryB-200 text-primaryB-800 font-bold px-6 py-3 rounded-2xl transition-all duration-200"
                >
                  See NGN pricing
                </Link>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ STATS BAR ━━━ */}
      <section className="border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-primaryB-200/60">
            {[
              { value: 'NGN 20k', label: 'per month' },
              { value: '1 month', label: 'Free trial' },
              { value: '100%', label: 'Offline capable' },
              { value: '5 min', label: 'Setup time' },
            ].map((stat) => (
              <div key={stat.label} className="py-6 sm:py-10 text-center px-2">
                <div className="text-xl sm:text-3xl font-extrabold text-primaryB-900 mb-0.5 sm:mb-1">{stat.value}</div>
                <div className="text-xs sm:text-sm text-primaryB-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ THE NIGERIA REALITY ━━━ */}
      <section className="py-16 sm:py-24 bg-primaryB-900">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-400 text-sm font-bold tracking-wide uppercase mb-4">The reality</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-5 max-w-2xl">
              Nigerian business owners have three problems most POS software ignores.
            </h2>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl mb-12">
              Power outages, cash reconciliation, and staff accountability are daily concerns. Smart POS Africa was built with all three in mind.
            </p>
          </R>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                problem: 'NEPA takes light',
                solution: 'Works 100% offline. Transactions are saved on the device and sync automatically when power and internet return.',
              },
              {
                problem: 'Customers pay by transfer',
                solution: 'Accepts cash, bank transfers, card, and mobile money. Split payments across methods in a single transaction.',
              },
              {
                problem: 'Cashier accountability',
                solution: 'Every sale is tied to the cashier who made it. Discounts are logged. Daily reports show exactly what happened on each shift.',
              },
            ].map((item, i) => (
              <R key={item.problem} delay={i * 100}>
                <div className="bg-white/[0.05] border border-white/[0.08] rounded-3xl p-6">
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wide mb-2">The problem</p>
                  <p className="text-white font-bold text-base mb-5">{item.problem}</p>
                  <p className="text-white/40 text-xs font-bold uppercase tracking-wide mb-2">The solution</p>
                  <p className="text-white/70 text-sm leading-relaxed">{item.solution}</p>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ FEATURES ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">What's included</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-3">
              Everything your Nigerian business needs
            </h2>
            <p className="text-primaryB-500 text-base sm:text-lg mb-12 max-w-xl">
              One plan. Every feature. No add-ons or per-user fees.
            </p>
          </R>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <R key={feature.title} delay={i * 60}>
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border border-primaryB-200/60 hover:shadow-card hover:border-primaryB-300/80 transition-all duration-200 group h-full">
                    <div className={`w-10 h-10 rounded-xl ${feature.accent} flex items-center justify-center mb-3.5 transition-transform duration-200 group-hover:scale-110`}>
                      <Icon weight="bold" className={`w-5 h-5 ${feature.iconColor}`} />
                    </div>
                    <h3 className="text-primaryB-900 font-bold text-sm mb-1.5">{feature.title}</h3>
                    <p className="text-primaryB-400 text-xs sm:text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </R>
              );
            })}
          </div>
          <R delay={100}>
            <div className="mt-8 text-center">
              <Link
                to="/features"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition-all duration-200"
              >
                See all features <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ BUSINESS TYPES ━━━ */}
      <section className="py-14 sm:py-20 border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight mb-3">
              Built for every type of Nigerian business
            </h2>
            <p className="text-primaryB-500 text-base mb-8 max-w-xl">
              From a single Lagos shop to multi-branch retail across Nigeria, Smart POS Africa scales with you.
            </p>
          </R>
          <R delay={80}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8">
              <span className="text-primaryB-300 text-sm font-semibold whitespace-nowrap shrink-0">Works for</span>
              <div className="flex flex-wrap gap-2">
                {[
                  'Retail shops', 'Restaurants', 'Suya spots & food stalls', 'Salons & barbershops',
                  'Pharmacies', 'Supermarkets', 'Mini marts', 'Boutiques & clothing',
                  'Electronics shops', 'Hardware stores', 'Wholesale distributors', 'Service businesses',
                ].map((type) => (
                  <span
                    key={type}
                    className="px-3.5 py-1.5 rounded-full bg-white border border-primaryB-200/60 text-primaryB-700 text-xs sm:text-sm font-medium"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ PRICING ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <R>
              <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-4">Transparent pricing</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primaryB-900 tracking-tight mb-5">
                Priced in Naira.<br />No USD conversion.
              </h2>
              <p className="text-primaryB-500 text-base sm:text-lg leading-relaxed mb-6">
                Unlike POS software billed in dollars, Smart POS Africa charges you in NGN. Fixed naira price – no surprises when the exchange rate moves.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'All features included – nothing locked behind upgrades',
                  'Unlimited staff accounts at no extra cost',
                  'Multi-branch support from NGN 14,000/month per extra branch',
                  'One month free trial – no credit card required',
                  'Cancel anytime',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-2" />
                    <span className="text-primaryB-700 text-sm">{point}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm transition-all duration-200"
              >
                View full pricing <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </R>
            <R delay={100}>
              <div className="bg-primaryB-900 rounded-3xl p-8 sm:p-10">
                <p className="text-white/50 text-sm mb-2">Starting from</p>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-4xl sm:text-5xl font-extrabold text-white">NGN 20,000</span>
                  <span className="text-white/50 pb-1">/month</span>
                </div>
                <p className="text-emerald-400 text-sm mb-8">or NGN 200,000/year – save 17%</p>
                <div className="space-y-2.5 mb-8">
                  {[
                    'Full POS with barcode scanning',
                    'Inventory management',
                    'Staff management & activity logs',
                    'Expense tracking',
                    'Real-time analytics & reports',
                    'Daily email summaries',
                    'Receipt printing',
                    'Cash, bank transfer & card payments',
                    'Works fully offline',
                  ].map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <span className="w-1 h-1 rounded-full bg-emerald-400 flex-shrink-0" />
                      <span className="text-white/70 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/signup"
                  className="block text-center bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3.5 rounded-2xl transition-all duration-200"
                >
                  Start free trial
                </Link>
              </div>
            </R>
          </div>
        </div>
      </section>

      {/* ━━━ STATES ━━━ */}
      <section className="py-14 sm:py-20 border-y border-primaryB-200/60 bg-primaryB-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">Coverage</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight mb-3">
              Serving businesses across all 36 states in Nigeria
            </h2>
            <p className="text-primaryB-500 text-base mb-8">
              From Lagos Island to Kano – Smart POS Africa works wherever your business is located.
            </p>
          </R>
          <R delay={80}>
            <div className="flex flex-wrap gap-2">
              {NIGERIA_STATES.map((state) => (
                <span
                  key={state}
                  className="bg-white border border-primaryB-200/60 text-primaryB-600 text-sm px-3 py-1.5 rounded-full"
                >
                  {state}
                </span>
              ))}
            </div>
          </R>
        </div>
      </section>

      {/* ━━━ FAQ ━━━ */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <R>
            <p className="text-emerald-600 text-xs font-bold tracking-widest uppercase mb-3">FAQ</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 tracking-tight mb-10">
              Questions from Nigerian business owners
            </h2>
          </R>
          <div className="divide-y divide-primaryB-200/60">
            {NIGERIA_FAQS.map((faq, i) => (
              <R key={faq.question} delay={i * 60}>
                <div className="py-6 sm:py-7">
                  <h3 className="text-base sm:text-lg font-bold text-primaryB-900 mb-3">{faq.question}</h3>
                  <p className="text-primaryB-500 text-sm sm:text-base leading-relaxed">{faq.answer}</p>
                </div>
              </R>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ FINAL CTA + FOOTER (shared dark wrapper prevents rounded-corner artifact) ━━━ */}
      <div className="bg-primaryB-900">
        <section className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <R>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-5">
                Run a tighter business in Nigeria today
              </h2>
              <p className="text-white/60 text-base sm:text-lg mb-8 max-w-lg mx-auto">
                Set up in 5 minutes. No hardware needed. One month free – no credit card required.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-7 py-3.5 rounded-2xl transition-all duration-200"
                >
                  Start free trial <ArrowRight weight="bold" className="w-4 h-4" />
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-white/[0.08] hover:bg-white/[0.12] text-white font-bold px-7 py-3.5 rounded-2xl transition-all duration-200"
                >
                  Learn more
                </Link>
              </div>
            </R>
          </div>
        </section>

        <LandingFooter />
      </div>
    </div>
  );
};

export default NigeriaPage;
